// lib/content-context.tsx
"use client";

import React, { createContext, useContext, useState, useEffect } from "react";
import {
  createDiscussionAction,
  getDiscussionsAction,
  createReplyAction,
  getDiscussionByIdAction,
  toggleLikeDiscussionAction,
} from "@/app/app/feed/actions";
import { authClient } from "@/lib/auth-client";

// Types based on updated schema
interface User {
  id: bigint;
  name: string;
  email: string;
  avatar?: string | null;
  role_id?: bigint | null;
  org_id?: number;
  roles?: {
    id: bigint;
    name: string;
    display_name: string;
  } | null;
  org?: {
    id: number;
    org_name: string;
    org_image?: string;
  } | null;
}

interface Tag {
  id: number;
  discussions_id: number;
  tags: string;
  created_at: Date;
  updated_at: Date;
  status: number;
}

interface Like {
  id: number;
  discussions_id: number;
  liked_by: bigint;
  liked_who: bigint | null;
  created_at: Date;
  updated_at: Date;
  status: number;
  users: User;
}

interface Reply {
  id: number;
  discussions_id: number;
  created_by: bigint;
  reply_to: bigint;
  content: string;
  created_at: Date;
  updated_at: Date;
  status: number;
  users_replies_created_byTousers: User;
  users_replies_reply_toTousers: User;
}

interface Discussion {
  id: number;
  post_id: number | null;
  body: string | null;
  created_at: Date | null;
  updated_at: Date | null;
  deleted_at: Date | null;
  likes: Like[];
  replies: Reply[];
  tags: Tag[];
  likesCount: number;
  commentsCount: number;
  likedUsers: Array<{
    id: string;
    name: string;
    email: string;
    avatar?: string | null;
    role?: string;
  }>;
  isLikedByUser: boolean;
}

interface Post {
  id: string;
  author: {
    id: bigint;
    name: string;
    email: string;
    avatar?: string | null;
    isVerified?: boolean;
    location?: string;
    username?: string;
    org_id?: number;
    org_name?: string;
    org_image?: string;
    role?: string;
  };
  category: string;
  content: string;
  tags: string[];
  images: string[];
  likesCount: number;
  commentsCount: number;
  sharesCount: number;
  bookmarksCount: number;
  isLiked: boolean;
  isBookmarked: boolean;
  createdAt: Date;
  likedUsers: Array<{
    id: string;
    name: string;
    avatar?: string | null;
    role?: string;
  }>;
}

interface ContentContextType {
  posts: Post[];
  discussions: Discussion[];
  loading: boolean;
  error: string | null;
  createPost: (
    content: string,
    category: string,
    tags: string[],
  ) => Promise<{ success: boolean; error?: string }>;
  likePost: (postId: string) => Promise<{ success: boolean; error?: string }>;
  bookmarkPost: (postId: string) => Promise<void>;
  getPostComments: (postId: string) => Comment[];
  addComment: (
    postId: string,
    content: string,
    replyTo?: string,
  ) => Promise<{ success: boolean; error?: string }>;
  fetchDiscussions: () => Promise<void>;
  fetchDiscussionById: (id: number) => Promise<Discussion | null>;
  likeDiscussion: (discussionId: number) => Promise<{ success: boolean; error?: string }>;
  currentUser: User | null;
}

const ContentContext = createContext<ContentContextType | undefined>(undefined);

export function ContentProvider({ children }: { children: React.ReactNode }) {
  const [discussions, setDiscussions] = useState<Discussion[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const session = await authClient.getSession();
        if (session?.data?.user) {
          setCurrentUser({
            id: BigInt(session.data.user.id),
            name: session.data.user.name || "User",
            email: session.data.user.email || "",
            avatar: session.data.user.image || null,
          });
        }
      } catch (err) {
        console.error("Error fetching user:", err);
      }
    };
    fetchUser();
  }, []);

  const fetchDiscussions = async () => {
    setLoading(true);
    setError(null);
    try {
      const userId = currentUser?.id;
      const result = await getDiscussionsAction({ userId });
      
      if (result.success && result.discussions) {
        setDiscussions(result.discussions);

        // Convert discussions to posts
        const convertedPosts = result.discussions.map((discussion) => {
          // Find the author - could be the first user who liked (or you need to track author differently)
          const authorLike = discussion.likes.find(like => 
            like.users && like.liked_by === like.users.id
          );
          const authorUser = authorLike?.users || null;
          
          // Get user's organization if they have org_id
          let org = null;
          if (authorUser?.org_id) {
            org = {
              id: authorUser.org_id,
              org_name: authorUser.org_name, // You might want to fetch actual org name
            };
          }

          // Extract tags as strings
          const tagStrings = discussion.tags?.map((tag) => tag.tags) || [];

          // Determine category from tags
          let category = "general";
          if (tagStrings.some(tag => tag.toLowerCase().includes("support"))) {
            category = "support";
          } else if (tagStrings.some(tag => tag.toLowerCase().includes("resource"))) {
            category = "resources";
          } else if (tagStrings.some(tag => tag.toLowerCase().includes("question"))) {
            category = "questions";
          }

          return {
            id: discussion.id.toString(),
            author: {
              id: authorUser?.id || BigInt(0),
              name: authorUser?.name || "Anonymous",
              email: authorUser?.email || "",
              avatar: authorUser?.avatar || null,
              isVerified: true,
              location: "Unknown",
              username: authorUser?.name?.toLowerCase().replace(/\s+/g, "_") || "anonymous",
              org_id: authorUser?.org_id,
              org_name: org?.org_name,
              org_image: org?.avatar,
              role: authorUser?.roles?.name || "User",
            },
            category,
            content: discussion.body || "",
            tags: tagStrings,
            images: [],
            likesCount: discussion.likesCount || 0,
            commentsCount: discussion.commentsCount || 0,
            sharesCount: 0,
            bookmarksCount: 0,
            isLiked: discussion.isLikedByUser || false,
            isBookmarked: false,
            createdAt: discussion.created_at || new Date(),
            likedUsers: discussion.likedUsers || [],
          } as Post;
        });

        setPosts(convertedPosts);
      } else {
        setError(result.error || "Failed to fetch posts");
      }
    } catch (err) {
      console.error("Error fetching discussions:", err);
      setError("An error occurred while fetching posts");
    } finally {
      setLoading(false);
    }
  };

  const fetchDiscussionById = async (id: number) => {
    try {
      const userId = currentUser?.id;
      const result = await getDiscussionByIdAction(id, userId);
      if (result.success) {
        return result.discussion!;
      }
      return null;
    } catch (err) {
      console.error("Error fetching discussion:", err);
      return null;
    }
  };

  const likeDiscussion = async (discussionId: number) => {
    try {
      if (!currentUser) {
        return { success: false, error: "Please login to like discussions" };
      }

      const result = await toggleLikeDiscussionAction(discussionId, currentUser.id);
      if (result.success) {
        // Update local state
        setDiscussions(prev => prev.map(discussion => {
          if (discussion.id === discussionId) {
            const wasLiked = discussion.isLikedByUser;
            const newLikesCount = wasLiked ? discussion.likesCount - 1 : discussion.likesCount + 1;
            
            // Update liked users list
            let newLikedUsers = [...discussion.likedUsers];
            if (wasLiked) {
              // Remove current user from liked users
              newLikedUsers = newLikedUsers.filter(user => 
                user.id !== currentUser.id.toString()
              );
            } else {
              // Add current user to liked users
              newLikedUsers.push({
                id: currentUser.id.toString(),
                name: currentUser.name,
                email: currentUser.email,
                avatar: currentUser.avatar,
                role: "User"
              });
            }

            return {
              ...discussion,
              likesCount: newLikesCount,
              isLikedByUser: !wasLiked,
              likedUsers: newLikedUsers
            };
          }
          return discussion;
        }));

        // Also update posts state
        setPosts(prev => prev.map(post => {
          if (post.id === discussionId.toString()) {
            const wasLiked = post.isLiked;
            const newLikesCount = wasLiked ? post.likesCount - 1 : post.likesCount + 1;
            
            // Update liked users list
            let newLikedUsers = [...post.likedUsers];
            if (wasLiked) {
              newLikedUsers = newLikedUsers.filter(user => 
                user.id !== currentUser.id.toString()
              );
            } else {
              newLikedUsers.push({
                id: currentUser.id.toString(),
                name: currentUser.name,
                avatar: currentUser.avatar,
                role: "User"
              });
            }

            return {
              ...post,
              likesCount: newLikesCount,
              isLiked: !wasLiked,
              likedUsers: newLikedUsers
            };
          }
          return post;
        }));
      }
      return result;
    } catch (err) {
      console.error("Error liking discussion:", err);
      return { success: false, error: "An error occurred while liking" };
    }
  };

  const createPost = async (
    content: string,
    category: string,
    tags: string[],
  ) => {
    setLoading(true);
    try {
      // Add category to tags if not already included
      const allTags = [...tags];
      if (category && !allTags.includes(category)) {
        allTags.push(category);
      }

      const result = await createDiscussionAction({
        body: content,
        tags: allTags,
        category,
      });

      if (result.success) {
        await fetchDiscussions();
        return { success: true };
      } else {
        const errorMsg = result.error || "Failed to create post";
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }
    } catch (err: any) {
      const errorMsg = err.message || "An error occurred while creating post";
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setLoading(false);
    }
  };

  const likePost = async (postId: string) => {
    return await likeDiscussion(parseInt(postId));
  };

  const bookmarkPost = async (postId: string) => {
    console.log("Bookmark post:", postId);
  };

  const addComment = async (
    postId: string,
    content: string,
    replyTo?: string,
  ) => {
    try {
      if (!currentUser) {
        return { success: false, error: "Please login to comment" };
      }

      const result = await createReplyAction({
        discussionId: parseInt(postId),
        replyText: content,
        replyTo: replyTo ? BigInt(replyTo) : currentUser.id,
      });

      if (result.success) {
        // Refresh discussions to show new comment
        await fetchDiscussions();
        return { success: true };
      }
      return { success: false, error: result.error };
    } catch (err: any) {
      console.error("Error adding comment:", err);
      return { success: false, error: err.message || "Failed to add comment" };
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchDiscussions();
    }
  }, [currentUser]);

  return (
    <ContentContext.Provider
      value={{
        posts,
        discussions,
        loading,
        error,
        createPost,
        likePost,
        bookmarkPost,
        getPostComments: () => [], // Simplified for now
        addComment,
        fetchDiscussions,
        fetchDiscussionById,
        likeDiscussion,
        currentUser,
      }}
    >
      {children}
    </ContentContext.Provider>
  );
}

export function useContent() {
  const context = useContext(ContentContext);
  if (context === undefined) {
    throw new Error("useContent must be used within a ContentProvider");
  }
  return context;
}